import React, { useState } from 'react';
import { IconButton } from '@mui/material';
import { ScheduleDate } from '../entities/ScheduleDate';
import * as S from './styles';

interface Props {
  value?: Date;
  onChange: (date: Date) => void;
}

export const SchedulePicker: React.FC<Props> = ({ value = new Date(), onChange }) => {
  const [currentDate, setCurrentDate] = useState(new ScheduleDate(value));
  const [viewDate, setViewDate] = useState(new Date(value));
  const [time, setTime] = useState(currentDate.toTimeString());

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const getWeekday = (date: Date) => new Date(date).getDay();

  const handleDayClick = (day: number) => {
    const selected = new Date(year, month, day);
    const updated = new ScheduleDate(selected).withTime(time);
    setCurrentDate(updated);
    onChange(updated.getRaw());
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value;
    setTime(newTime);
    const updated = currentDate.withTime(newTime);
    setCurrentDate(updated);
    onChange(updated.getRaw());
  };

  const handleDateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dateString = e.target.value;
    const updated = currentDate.withDate(dateString);
    setCurrentDate(updated);
    setViewDate(updated.getRaw());
    onChange(updated.getRaw());
  };

  const renderCalendarDays = () => {
    const days = [];
    const firstDayOfMonth = new Date(year, month, 1);
    const offset = getWeekday(firstDayOfMonth);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i < offset; i++) {
      days.push(<div key={`empty-${i}`} />);
    }
    for (let day = 1; day <= getDaysInMonth(year, month); day++) {
      const date = new Date(year, month, day);
      date.setHours(0, 0, 0, 0);
      const isSelected =
        currentDate.getRaw().getFullYear() === year &&
        currentDate.getRaw().getMonth() === month &&
        currentDate.getRaw().getDate() === day;
      const isPast = date < today;
      days.push(
        <S.DayCell
          key={day}
          selected={isSelected}
          disabled={isPast}
          onClick={!isPast ? () => handleDayClick(day) : undefined}
          style={{
            opacity: isPast ? 0.4 : 1,
            cursor: isPast ? 'default' : 'pointer',
          }}
        >
          {day}
        </S.DayCell>
      );
    }
    return days;
  };

  const changeMonth = (delta: number) => {
    const newDate = new Date(viewDate);
    newDate.setMonth(newDate.getMonth() + delta);
    setViewDate(newDate);
  };

  return (
    <S.Wrapper>
      <S.CalendarSection>
        <S.CalendarHeader>
          <IconButton onClick={() => changeMonth(-1)}>{'‹'}</IconButton>
          <h3>
            {viewDate.toLocaleString('default', {
              month: 'long',
              year: 'numeric',
            })}
          </h3>
          <IconButton onClick={() => changeMonth(1)}>{'›'}</IconButton>
        </S.CalendarHeader>
        <S.CalendarGrid>
          {['do', '2ª', '3ª', '4ª', '5ª', '6ª', 'sá'].map((d) => (
            <strong key={d} style={{ textAlign: 'center', fontSize: '0.8rem' }}>
              {d}
            </strong>
          ))}
          {renderCalendarDays()}
        </S.CalendarGrid>
      </S.CalendarSection>
      <S.InputSection>
        <input
          type="date"
          value={currentDate.toISOStringDate()}
          onChange={handleDateInputChange}
        />
        <input
          type="time"
          step="1"
          value={time}
          onChange={handleTimeChange}
        />
      </S.InputSection>
    </S.Wrapper>
  );
};
